import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import logger from '../utils/logger';
import Header from './Header';  // Import the Header component
import { FaTrashAlt } from 'react-icons/fa';  // Import trash icon
import '../styles/StudentSubmissionsList.scss'; // Create this file later

function StudentSubmissionsList() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { authToken, userData } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSubmissions = async () => {
      setLoading(true);
      setError(null);
      if (!authToken) {
          setError("Authentication required.");
          setLoading(false);
          return;
      }
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/submissions/student`, {
          headers: {
            'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
        });

        // First log the raw response for debugging
        const responseText = await response.text();
        logger.debug('Raw API response:', responseText);

        if (!response.ok) {
          try {
            const errorData = JSON.parse(responseText);
            throw new Error(errorData.error || `Failed to fetch submissions: ${response.status}`);
          } catch (parseError) {
            throw new Error(`Server error: ${response.status} - ${responseText.slice(0, 100)}...`);
          }
        }

        // Try to parse the response as JSON
        try {
          const data = JSON.parse(responseText);
          setSubmissions(data);
        } catch (parseError) {
          logger.error('Error parsing JSON response:', parseError);
          throw new Error('Invalid response format from server');
        }
      } catch (err) {
        logger.error("Error fetching student submissions:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSubmissions();
  }, [authToken]);

  const handleViewDetails = (submissionId) => {
    navigate(`/submission/${submissionId}`);
  };

  const handleShareWithTeacher = async (submissionId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/submissions/${submissionId}/visibility`,
        {
          method: 'POST',
          headers: {
            'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ visible: true })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to share submission');
      }

      // Update local state to reflect the change
      setSubmissions(prevSubmissions =>
        prevSubmissions.map(sub =>
          sub.submission_id === submissionId
            ? { ...sub, visible_to_teacher: true }
            : sub
        )
      );
    } catch (err) {
      logger.error('Error sharing submission:', err);
      setError('Failed to share submission with teacher');
    }
  };

  const handleDeleteSubmission = async (submissionId, e) => {
    e.stopPropagation(); // Prevent triggering the view details click

    // Confirm deletion
    if (!window.confirm('Are you sure you want to delete this submission? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/submissions/${submissionId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete submission');
      }

      // Remove the deleted submission from state
      setSubmissions(prevSubmissions => 
        prevSubmissions.filter(sub => sub.submission_id !== submissionId)
      );
    } catch (err) {
      logger.error('Error deleting submission:', err);
      setError('Failed to delete submission');
    }
  };

  if (loading) {
    // Consider using LoadingAnecdotes or a simpler spinner
    return <div className="loading">Loading your submissions...</div>;
  }

  return (
    <div className="student-submissions-list">
      <Header onNewEssay={() => navigate('/')} />
      <nav className="dashboard-nav">
        <h1>My Comprehension Submissions</h1>
        <div>
          <Link 
            to="/dashboard"
            className="back-button dashboard-back-btn"
          >
            Go Back
          </Link>
        </div>
      </nav>

      {error && <div className="error-message">{error}</div>}
      
      {submissions.length === 0 ? (
        <p className="no-submissions">You haven't submitted any comprehensions yet.</p>
      ) : (
        <div className="submissions-grid">
          {submissions.map((sub) => (
            <div key={sub.submission_id} className="submission-tile">
              <div className="submission-content" onClick={() => handleViewDetails(sub.submission_id)}>
                <div className="submission-header">
                  <h4>{sub.comprehension_title}</h4>
                  {!sub.visible_to_teacher && (
                    <button 
                      onClick={(e) => handleDeleteSubmission(sub.submission_id, e)}
                      className="delete-submission-button"
                      title="Delete submission"
                    >
                      <FaTrashAlt />
                    </button>
                  )}
                </div>
                <p>Submitted: {new Date(sub.submitted_at).toLocaleDateString()}</p>
                {sub.score !== null && typeof sub.score !== 'undefined' && (
                   <p className="score">Score: {sub.score}%</p>
                )}
                {sub.score === null && (
                   <p className="score pending">Score: Pending</p>
                )}
              </div>
              <div className="submission-actions">
                {!sub.visible_to_teacher ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShareWithTeacher(sub.submission_id);
                    }}
                    className="share-button"
                  >
                    Share with Teacher
                  </button>
                ) : (
                  <span className="shared-status">✓ Shared with Teacher</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default StudentSubmissionsList; 